<template>
	<div class="timeline-scrollbar-wrapper" @pointerdown.stop>
		<div class="timeline-scrollbar-track" ref="scrollbarTrack" @pointerdown="onScrollbarTrackPointerDown">
			<div
				class="timeline-scrollbar-thumb"
				ref="scrollbarThumb"
				:style="scrollbarThumbStyle"
				@pointerdown.prevent="onScrollbarPointerDown"
				role="slider"
				tabindex="0"
			></div>
		</div>
	</div>
</template>

<script>
import {
	releasePointerOnPointerUp,
	updateScrollbarPointerDown,
	updateViewportOnPointerMove,
	updateViewportOnTrackPointerDown
} from './waveformScrollbarFunctions';

export default {
	name: 'AudioScrollbar',
	props: {
		viewportStartSec: { type: Number },
		viewportEndSec: { type: Number },
		audioDuration: { type: Number },
		updateKeyForSyncScrollbar: { type: Number }
	},
	data() {
		return {
			isDraggingThumb: false,
			dragStartX: 0,
			thumbStartLeft: 0,
			lastScrollbarWidth: 0
		};
	},
	computed: {
		viewportFraction() {
			const visible = Math.max(0.000001, this.viewportEndSec - this.viewportStartSec);
			return visible / Math.max(0.000001, this.audioDuration);
		},
		scrollbarThumbStyle() {
			const leftPercentage = (this.viewportStartSec / Math.max(1, this.audioDuration)) * 100;
			const widthPercentage = Math.max(0.5, this.viewportFraction * 100);
			return {
				left: leftPercentage + '%',
				width: widthPercentage + '%'
			};
		}
	},
	mounted() {
		this.addListeners();
	},
	beforeDestroy() {
		this.removeListeners();
	},
	watch: {
		updateKeyForSyncScrollbar: {
			handler() {
				this.syncScrollbarAfterViewportChange();
			}
		}
	},
	methods: {
		addListeners() {
			window.addEventListener('pointerup', this.onScrollbarPointerUp);
			window.addEventListener('pointermove', this.onScrollbarPointerMove);
		},
		removeListeners() {
			window.removeEventListener('pointerup', this.onScrollbarPointerUp);
			window.removeEventListener('pointermove', this.onScrollbarPointerMove);
		},
		updateScrollbar() {
			const track = this.$refs.scrollbarTrack;
			if (!track) {
				return;
			}
			const trackWidth = track.clientWidth;
			if (trackWidth !== this.lastScrollbarWidth) this.lastScrollbarWidth = trackWidth;
		},
		onScrollbarPointerDown(event) {
			event.preventDefault();
			this.isDraggingThumb = true;
			const { dragStartX, thumbStartLeft } = updateScrollbarPointerDown(event, this.$refs.scrollbarThumb);
			this.dragStartX = dragStartX;
			this.thumbStartLeft = thumbStartLeft;
		},
		onScrollbarPointerMove(event) {
			if (!this.isDraggingThumb) return;

			const track = this.$refs.scrollbarTrack;
			if (!track) return;

			const thumb = this.$refs.scrollbarThumb;
			const viewport = {
				fraction: this.viewportFraction,
				startSec: this.viewportStartSec,
				endSec: this.viewportEndSec
			};

			const { viewportStartSec, viewportEndSec } = updateViewportOnPointerMove(
				event,
				track,
				thumb,
				this.dragStartX,
				viewport,
				this.thumbStartLeft,
				this.audioDuration
			);

			this.$emit('viewport-change', { startSec: viewportStartSec, endSec: viewportEndSec });
		},
		onScrollbarPointerUp(event) {
			if (!this.isDraggingThumb) return;

			this.isDraggingThumb = false;
			try {
				const thumb = this.$refs.scrollbarThumb;
				releasePointerOnPointerUp(event, thumb);
			} catch (err) {}
		},
		onScrollbarTrackPointerDown(event) {
			const track = this.$refs.scrollbarTrack;
			if (!track) return;

			const viewport = {
				startSec: this.viewportStartSec,
				endSec: this.viewportEndSec
			};
			const { viewportStartSec, viewportEndSec } = updateViewportOnTrackPointerDown(
				event,
				track,
				viewport,
				this.audioDuration
			);

			this.$emit('viewport-change', { startSec: viewportStartSec, endSec: viewportEndSec });
		},
		syncScrollbarAfterViewportChange() {
			this.$nextTick(this.updateScrollbar);
		}
	}
};
</script>

<style scoped>
.timeline-scrollbar-wrapper {
	padding-top: 8px;
	user-select: none;
}

.timeline-scrollbar-track {
	height: 10px;
	background: rgba(255, 255, 255, 0.04);
	border-radius: 6px;
	position: relative;
	width: 100%;
}

.timeline-scrollbar-thumb {
	position: absolute;
	top: 1px;
	height: 8px;
	background: linear-gradient(90deg, #2196f3, #1976d2);
	border-radius: 6px;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);
	cursor: grab;
	touch-action: none;
}
.timeline-scrollbar-thumb:active {
	cursor: grabbing;
}
</style>
